import { getOrganizationOfUser } from '@upright/common/iam/organizations/common';
import { getMostSpecificUserGroup } from '@upright/common/iam/user-groups/utils';
import { UserPrivate } from '@upright/common/iam/users/types';
import { useEffect, useRef } from 'react';
import { Datum } from './types';
import { SerializedPayload } from './use-push-gtm-event/payloads';
import { isGoogleTagManagerEnabled } from './utils';

// Adding property 'dataLayer' to 'Window' for the sake of GTM.
declare global {
  interface Window {
    dataLayer?: Array<Datum | SerializedPayload>;
  }
}

interface Props {
  gtmId: string;
  loggedInUser?: UserPrivate;
}

export const GoogleTagManager = (props: Props) => {
  const { loggedInUser, gtmId } = props;
  const enableGTM = isGoogleTagManagerEnabled(loggedInUser);

  // Inject GTM script only once.
  const isScriptInjected = useRef(false);

  useEffect(() => {
    // Push GTM login event upon getting an instantiated logged-in user.
    if (enableGTM && loggedInUser?.id !== undefined) {
      // NR: wrap GTM's event-sending in a try-catch block in order to avoid race conditions or other hard-to-test bugs.
      try {
        if (!window.dataLayer) {
          throw Error(`dataLayer has not been initialized. User ${loggedInUser.id}.`);
        }
        const organization = getOrganizationOfUser(loggedInUser);
        const userGroup = getMostSpecificUserGroup(loggedInUser.groups);
        window.dataLayer.push({
          event: 'login_id',
          user: [
            {
              userId: loggedInUser.id,
              userGroupId: userGroup?.id,
              userGroupName: userGroup?.name,
              organizationId: organization?.id,
              organizationName: organization?.name,
              userSegment: organization?.type,
            },
          ],
        });
      } catch (error) {
        console.error('Failed pushing GTM login event', error);
      }
    }
    // We break the rules of hooks here, because we want to push the login event to GTM only once.
    // In the theoretical case that the user's groups & organization change during a single session,
    // it's perfectly fine that we don't update that change to GTM.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableGTM, loggedInUser?.id]);

  if (enableGTM && !isScriptInjected.current) {
    injectGtm(gtmId);
    isScriptInjected.current = true;
  }

  return (
    <>
      {enableGTM && (
        // Place GTM code snippet in body.
        <noscript>
          {`<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`}
        </noscript>
      )}
    </>
  );
};

function injectGtm(gtmId: string) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' } as any);
  const scriptElement = document.getElementsByTagName('script')[0];
  const gtmElement = document.createElement('script');
  gtmElement.async = true;
  gtmElement.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  scriptElement.parentNode!.insertBefore(gtmElement, scriptElement);
}
